
// @ is an alias to /src
import { defineComponent, ref } from "vue";
import { required, requiredIf, helpers } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import validateSsn from "@/utils/validations/validateSsn";
import { vMaska } from "maska";
import messages from "@/config/messages";
import AppFooter from "@/components/common/AppFooter.vue";
import regexExps from "@/config/regexExps";
import helper from "@/utils/helper";
import common from "@/config/common";
import { mapMutations, mapGetters } from "vuex";
import routesPath from "@/config/routesPath";
import * as apiClient from "@/apiclient/";
import eventBus from "@/utils/eventBus";

export default defineComponent({
  name: "ProfileInfoVIew",
  components: {
    AppFooter,
  },
  directives: { maska: vMaska },
  setup() {
    return { v$: useVuelidate() };
  },
  data: () => {
    return {
      profile: {} as apiClient.ProfileResponseDto,
      behalfProfile: {} as apiClient.ProfileResponseDto,
      originalProfile: {} as apiClient.ProfileResponseDto,
      updateProfile: {} as apiClient.UpdateProfileDto,
      userSSN: "",
      messages,
      allCountries: [] as apiClient.CountryResponseDto[],
      allStates: [] as apiClient.StateResponseDto[],
      allStatesOptions: [] as apiClient.StateResponseDto[],
      hideAddressFields: false,
      isFormInvalid: false,
      isSubmitDisabled: false,
      validationMsg: "Oops, Something went wrong.",
      genderData: [] as apiClient.GenderDto[],
      common,
      countries: {} as any,
      enrollmentType: 0,
    };
  },
  validations() {
    return {
      profile: {
        firstName: { required },
        lastName: { required },
        dob: {
          required,
          customAge: helpers.withParams(
            { type: "customAge" },
            (value: string) => {
              const dobDate = new Date(value);
              const today = new Date();
              const minAllowedAge = 14;
              const minAllowedDate = new Date(
                today.getFullYear() - minAllowedAge,
                today.getMonth(),
                today.getDate()
              );
              return dobDate <= minAllowedDate;
            }
          ),
        },
        genderId: { required },
        ssn: {
          required,
          validateSsn,
        },
        phoneNumber: {
          validPhone: helpers.withParams(
            { type: "validPhone" },
            (value: string) => {
              if (!value) {
                return true;
              }
              if (this.isCountryOther) {
                const regexValue = new RegExp(regexExps.notOtherCountryPhone);

                return regexValue.test(value);
              } else {
                const regexValue = new RegExp(regexExps.otherCountryPhone);

                return regexValue.test(value);
              }
            }
          ),
        },
        countryId: { required },
        address1: { required },
        city: {
          requiredIfCountryNotOther: requiredIf(this.isCountryOther),
        },
        geoStateId: {
          requiredIfCountryNotOther: requiredIf(this.isCountryOther),
          notZero: helpers.withParams(
            { type: "notZero" },
            (stateId: number) => {
              if (this.isCountryOther) return stateId > 0;
              return true;
            }
          ),
        },
        zipCode: {
          requiredIfCountryNotOther: requiredIf(this.isCountryOther),
          validZip: helpers.withParams(
            { type: "validZip" },
            (value: string) => {
              let regExp = "";
              if (this.profile.countryId == this.countries.USA) {
                regExp = regexExps.zipcodeUS;
              } else if (this.profile.countryId == this.countries.CAN) {
                regExp = regexExps.zipcodeCanada;
              }
              const regexValue = new RegExp(regExp);

              return regexValue.test(value);
            }
          ),
        },
        originalEmployee: { required },
      },
    };
  },
  computed: {
    ...mapGetters(["getFormDirty", "getEnrollment"]),
    otherApplicant: {
      get() {
        return !this.profile.originalEmployee;
      },
      async set(value: boolean) {
        if (value) {
          if (this.behalfProfile.originalEmployee === undefined) {
            this.profile.firstName = "";
            this.profile.lastName = "";
            this.profile.dob = "";
            this.profile.genderId = 0;
            this.profile.ssn = "";
            this.profile.phoneNumber = "";
            this.profile.countryId = 0;
            this.profile.address1 = "";
            this.profile.address2 = "";
            this.profile.city = "";
            this.profile.geoStateId = 0;
            this.profile.zipCode = "";
            this.hideAddressFields = false;
            this.v$.profile.$reset();
            this.allStatesOptions = [];
          } else {
            this.hideAddressFields = false;
            this.profile = this.behalfProfile;
            if (
              this.profile.countryId!.toString() ===
              this.countries.OTH.toString()
            ) {
              this.hideAddressFields = true;
            } else {
              this.getStateOptions();
            }
          }
        } else {
          this.setOriginalProfileInfo();
          this.touchFieldsWithErrors();
        }
        this.profile.originalEmployee = !value;
      },
    },
    isCountryOther() {
      const res = this.profile.countryId == this.countries.OTH ? false : true;
      return res;
    },
  },
  async created() {
    var enrollment = this.getEnrollment;
    this.enrollmentType = enrollment.serviceType;
    eventBus.emit("toggle-enrollments-menu", false);
    await this.getGender();
    await this.getLocations();
    await this.getProfileInfoData();
    await this.getOriginalProfileInfo();
    for (let field in this.profile) {
      if (
        field !== "electionId" &&
        field !== "status" &&
        field !== "emailAddress" &&
        field !== "enrollmentDate" &&
        field !== "address2"
      ) {
        this.$watch(
          () => this.v$.profile[field].$dirty,
          (newDirty) => {
            if (newDirty) {
              this.setFormDirty(true);
            }
          }
        );
      }
    }
    window.addEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.confirmBeforeUnload);
  },
  beforeRouteLeave(to, from, next) {
    helper.beforeRouterLeave(next);
  },
  methods: {
    ...mapMutations(["setFormDirty"]),
    confirmBeforeUnload(event: any) {
      helper.confirmBeforeUnload(event);
    },
    setAddressDirty() {
      this.setFormDirty(true);
    },
    touchFieldsWithErrors() {
      const allErrors = this.v$.profile.$silentErrors;
      for (const field of allErrors) {
        this.v$.profile[field.$property].$touch();
      }
    },
    async getLocations() {
      const { allCountries, allStates, countries } =
        await helper.getLocations();

      this.allCountries = allCountries;
      this.allStates = allStates;
      this.countries = countries;
    },
    async getGender(): Promise<void> {
      const result = await apiClient
        .userController()
        .getApiUserGender(this.getEnrollment.customerParticipantId);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.genderData = result.data;
        this.isFormInvalid = false;
      }
    },
    changeCountry() {
      this.profile.address1 = "";
      this.profile.address2 = "";
      this.profile.city = "";
      this.profile.geoStateId = 0;
      this.profile.zipCode = "";
      this.v$.profile.address1.$reset();
      this.v$.profile.city.$reset();
      this.v$.profile.geoStateId.$reset();
      this.v$.profile.zipCode.$reset();
      this.getStateOptions();
      this.v$.profile.countryId.$touch();
    },
    getStateOptions() {
      this.hideAddressFields = false;
      if (this.profile.countryId == this.countries.OTH) {
        this.hideAddressFields = true;
      }
      this.allStatesOptions = this.allStates.filter((state) => {
        return this.profile.countryId === state.countryId;
      });
    },
    async getProfileInfoData() {
      const params = {
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient.profileController().getApiProfile(params);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.profile = result.data;
        this.profile.dob = helper.formatDate(this.profile.dob!, "YYYY-MM-DD");
        this.setSelectDefaultValue(this.profile);
        if (!this.profile.originalEmployee) {
          this.behalfProfile = this.profile;
        }
        this.getStateOptions();
      }
    },
    async getOriginalProfileInfo() {
      const params = {
        originalEmployee: this.profile.originalEmployee,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .profileController()
        .getApiProfilePrimary(params);
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        this.originalProfile = result.data;
        this.userSSN = this.originalProfile.ssn!;
      }
    },
    setOriginalProfileInfo() {
      this.profile = { ...this.originalProfile };
      this.profile.dob = helper.formatDate(this.profile.dob!, "YYYY-MM-DD");
      this.setSelectDefaultValue(this.originalProfile);
      this.getStateOptions();
    },
    setSelectDefaultValue(profileDate: any) {
      this.profile.genderId =
        profileDate.genderId === null ? "" : profileDate.genderId;
      this.profile.countryId =
        profileDate.countryId === null ? "" : profileDate.countryId;
      this.profile.geoStateId =
        profileDate.geoStateId === null ? "" : profileDate.geoStateId;
    },
    async submitHandler() {
      this.v$.profile.$touch();
      if (this.v$.profile.$invalid || this.isSubmitDisabled) {
        return;
      }
      this.isSubmitDisabled = true;

      this.updateProfile = {
        electionId: this.profile.electionId,
        firstName: this.profile.firstName,
        lastName: this.profile.lastName,
        countryId: this.profile.countryId!,
        address1: this.profile.address1,
        address2: this.profile.address2,
        city: this.profile.city,
        geoStateId: this.profile.geoStateId!,
        zipCode: this.profile.zipCode,
        genderId: this.profile.genderId!,
        phoneNumber: this.profile.phoneNumber,
        ssn: this.profile.ssn,
        originalEmployee: this.profile.originalEmployee,
        dob: this.profile.dob,
        customerParticipantId: this.getEnrollment.customerParticipantId,
      };
      const result = await apiClient
        .profileController()
        .putApiProfile(this.updateProfile);
      this.isSubmitDisabled = false;
      if (!result || result.error) {
        this.isFormInvalid = true;
        if (result.error) {
          this.validationMsg = result.error + messages.errorMsg;
        }
        return;
      } else if (result.data) {
        helper.setPageStep(common.pageStep.profileInformation);
        this.isFormInvalid = false;
        this.setFormDirty(false);
        this.$router.push({
          name: routesPath.dependents,
        });
      }
    },
  },
});
